import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Link, Stack, Tooltip, Typography } from '@mui/material';
import {
  AirportShuttle,
  DirectionsCar,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  LocalShipping,
} from '@mui/icons-material';

import {
  CarGroup,
  CarOffer,
  carBody,
  categoryLabel,
  hasAirConditioning,
  isOnTerminal,
  mileageLabel,
  parseCount,
  transmissionLabel,
} from '../cars';
import { useAudience } from '../audience';
import { PriceBlock } from './PriceBlock';
import { FareConditionsList } from './FareConditionsList';
import { FareRow } from './FareRow';
import { MetaLine } from './MetaLine';
import { ResultMedia } from './ResultMedia';
import { StatusFlag } from './StatusFlag';
import { RuteroResultCard } from './RuteroResultCard';

interface Props {
  group: CarGroup;
  /** Días del alquiler: el precio del proveedor es el TOTAL del período. */
  days: number;
  bookingDisabled?: boolean;
  /** Por qué está bloqueada la reserva (vencida, o búsqueda sin ejecutar). */
  bookingDisabledReason?: string;
  /** Permite que una generación expandida venza sin bloquear las tarifas base. */
  bookingDisabledReasonForOffer?: (offer: CarOffer) => string | undefined;
  onBook: (offer: CarOffer) => void;
  /** Posición en la lista de resultados: da el escalonado de la entrada. */
  index?: number;
}

const BODY_ICONS = {
  car: DirectionsCar,
  suv: DirectionsCar,
  wagon: DirectionsCar,
  convertible: DirectionsCar,
  van: AirportShuttle,
  pickup: LocalShipping,
} as const;

/**
 * Un vehículo con sus PLANES de cobertura. El proveedor cotiza el mismo auto
 * varias veces (Just Car / Basic Protection / Full Protection): el vehículo se
 * muestra una vez y los planes bajan a la franja, una fila cada uno con su
 * precio —que es el TOTAL del alquiler— y su propio botón.
 */
export function CarOfferCard({
  group,
  days,
  bookingDisabled,
  bookingDisabledReason,
  bookingDisabledReasonForOffer,
  onBook,
  index,
}: Props) {
  const { t, i18n } = useTranslation();
  const { isAgent } = useAudience();
  const [openConditions, setOpenConditions] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);

  const { cheapest: offer, rates } = group;
  const BodyIcon = BODY_ICONS[carBody(offer)];

  const passengers = parseCount(offer.passengers);
  const bags = parseCount(offer.bags);
  const doors = parseCount(offer.doors);
  const transmission = transmissionLabel(offer.transmission, i18n.language);
  const mileage = mileageLabel(offer.kmIncluded, i18n.language);
  const onTerminal = isOnTerminal(offer);
  const companyLogoUrl = logoFailed ? undefined : offer.companyLogoUrl;

  /** Specs del vehículo: texto quieto, no una fila de chips. */
  const specs = [
    [categoryLabel(offer.category, i18n.language), offer.sippCode]
      .filter(Boolean)
      .join(' '),
    passengers ? t('travelServices.cars.passengers', { count: passengers }) : undefined,
    bags ? t('travelServices.cars.bags', { count: bags }) : undefined,
    doors ? t('travelServices.cars.doors', { count: doors }) : undefined,
    transmission,
    hasAirConditioning(offer.airConditioning)
      ? t('travelServices.cars.airConditioning')
      : undefined,
    mileage,
  ];

  const offices =
    offer.pickUpLocation === offer.dropOffLocation
      ? t('travelServices.cars.sameOffice', { office: offer.pickUpLocation })
      : t('travelServices.cars.twoOffices', {
        pickUp: offer.pickUpLocation,
        dropOff: offer.dropOffLocation,
      });

  /**
   * Comisionable: el precio ES el PVP al cliente y la comisión la liquida el
   * proveedor aparte — el monto exacto recién se conoce al reservar, así que
   * acá se explica sin inventar cifras. Va UNA vez, en la columna.
   */
  const commissionableNote = (
    <Tooltip title={t('travelServices.cars.commissionableTooltip')}>
      <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
        <InfoOutlined sx={{ fontSize: 14 }} color="disabled" />
        <Typography variant="caption" color="text.secondary">
          {t('travelServices.cars.commissionable')}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <RuteroResultCard
      index={index}
      media={
        <ResultMedia
          photo={offer.carImageUrl}
          icon={<BodyIcon sx={{ fontSize: 44 }} color="action" />}
          alt={offer.carModel ?? ''}
        />
      }
      header={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {companyLogoUrl ? (
            <Box
              component="img"
              src={companyLogoUrl}
              alt={offer.company ?? ''}
              loading="lazy"
              sx={{ height: 18, maxWidth: 72, objectFit: 'contain' }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
              {offer.company}
            </Typography>
          )}
          <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
            {offices}
          </Typography>
          {/* Retirar con shuttle vs. en la terminal cambia la experiencia del
              pasajero: se dice ANTES de reservar, no al llegar. */}
          {offer.terminalAccess && (
            <Box ml="auto">
              <Tooltip
                title={t(
                  onTerminal
                    ? 'travelServices.cars.onTerminalTooltip'
                    : 'travelServices.cars.shuttleTooltip',
                )}
              >
                <Box component="span" sx={{ cursor: 'help' }}>
                  <StatusFlag
                    tone={onTerminal ? 'info' : 'neutral'}
                    label={t(
                      onTerminal
                        ? 'travelServices.cars.onTerminal'
                        : 'travelServices.cars.shuttle',
                    )}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}
        </Stack>
      }
      title={
        <Typography sx={{ fontSize: 15.5, fontWeight: 600 }} noWrap title={offer.carModel ?? '—'}>
          {offer.carModel ?? '—'}
        </Typography>
      }
      details={<MetaLine items={specs} />}
      price={
        <PriceBlock
          pricing={offer.pricing}
          fromLabel={rates.length > 1 ? t('travelServices.priceFrom') : undefined}
          unit={t('travelServices.cars.totalForDays', { count: days })}
          grossNote={isAgent ? commissionableNote : null}
        />
      }
      fares={rates.map((rate, position) => {
        const rateKey = `${rate.provenance.slice(0, 12)}-${position}`;
        const conditions = rate.fareConditions ?? [];
        const open = openConditions === rateKey;
        const rateBlockedReason = bookingDisabledReasonForOffer?.(rate);
        return (
          <FareRow
            key={rateKey}
            label={rate.rateName ?? t('travelServices.cars.ratePlan')}
            detail={
              <>
                {rate.rateDescription}
                {conditions.length > 0 && (
                  <>
                    {rate.rateDescription ? ' · ' : ''}
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      underline="always"
                      sx={{ verticalAlign: 'baseline' }}
                      onClick={() =>
                        setOpenConditions((current) => (current === rateKey ? null : rateKey))}
                    >
                      {t('travelServices.cars.included')}
                      {open ? (
                        <ExpandLess sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />
                      )}
                    </Link>
                  </>
                )}
              </>
            }
            pricing={rate.pricing}
            // Ya está dicho en la columna: repetirlo por fila es ruido.
            grossNote={null}
            actionLabel={t('travelServices.book')}
            onAction={() => onBook(rate)}
            disabledReason={rateBlockedReason ?? (bookingDisabled ? bookingDisabledReason : undefined)}
          >
            {conditions.length > 0 && (
              <Collapse in={open} unmountOnExit timeout={0}>
                <Box mt={0.5}>
                  <FareConditionsList
                    conditions={conditions}
                    typeLabelPrefix="travelServices.cars.conditionTypes"
                  />
                </Box>
              </Collapse>
            )}
          </FareRow>
        );
      })}
    />
  );
}
